/* eslint-disable replo/no-tailwind-literal-color */
import type { Editor } from "@tiptap/react";
import type { ChatMessage as ChatMessageType } from "schemas/generated/chatMessage";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { fileVersionRouter } from "@editor/trpc/fileVersion";
import Image from "@svg/chat/Image";
import Megaphone from "@svg/chat/Megaphone";
import Message from "@svg/chat/Message";
import Page from "@svg/chat/Page";
import Paintbrush from "@svg/chat/Paintbrush";
import Shirt from "@svg/chat/Shirt";
import Sparkle from "@svg/chat/Sparkle";

import {
  useIsChatLoading,
  useMessagesForChat,
} from "@/features/react-editor/components/chat/store/ChatStoreContext";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { parseJsonOrNull } from "replo-utils/lib/json";

import { ChatActions } from "./ChatActions";
import { ChatMessage } from "./ChatMessage";
import { IconWrapper } from "./IconWrapper";
import { TypingIndicator } from "./TypingIndicator";

type MessageGroup = {
  role: "user" | "assistant";
  messages: ChatMessageType[];
  toolMessageId?: string;
};

interface MessageListProps {
  editor: Editor | null;
  isTyping?: boolean;
  onQuickPrompt?: (prompt: string, action?: string) => void;
}

const PlaceholderOption = ({
  icon,
  title,
  subtitle,
  prompt,
  action,
  editor,
  onAction,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  prompt: string;
  action?: string;
  editor: Editor | null;
  onAction?: (prompt: string, action?: string) => void;
}) => {
  return (
    <div
      className="flex flex-col border border-border rounded-lg p-3 hover:bg-sheet cursor-pointer select-text relative transition-colors"
      onClick={() => {
        if (onAction) {
          onAction(prompt, action);
        } else {
          editor?.commands.setContent(prompt);
        }
      }}
    >
      <div className="mb-2 pointer-events-auto">
        <IconWrapper size="base">{icon}</IconWrapper>
      </div>
      <div className="text-base font-medium text-muted pointer-events-auto">
        {title}
      </div>
      <div className="text-sm text-placeholder pointer-events-auto">
        {subtitle}
      </div>
    </div>
  );
};

export function MessageList({
  isTyping = false,
  editor,
  onQuickPrompt,
}: MessageListProps) {
  const logEvent = useLogAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chatId");
  const messages = useMessagesForChat(chatId);
  const isChatLoading = useIsChatLoading(chatId);
  const groupedMessages = groupMessages(messages);

  const pageId = searchParams.get("pageId");
  const currentPlaceholderOptions = pageId
    ? pagePlaceholderOptions
    : placeholderOptions;

  const lastEditedPageId = useFindLastEditedPageIdFromMessages(messages);

  // NOTE (Bobby, 2025-07-30): Auto-sets pageId URL param when switching chats based on last edited page.
  // Uses ref to track chat:page combinations to prevent overriding manual page switches and handle
  // trpc query timing (lastEditedPageId loads after chatId changes).
  const lastProcessedRef = React.useRef<string | null>(null);

  /**
   * Updates search params to set or remove pageId based on lastEditedPageId
   */
  const updatePageIdParams = React.useCallback(
    (lastEditedPageId: string | null) => {
      setSearchParams((prev) => {
        if (lastEditedPageId && prev.get("pageId") !== lastEditedPageId) {
          // Set pageId if there's a lastEditedPageId
          if (prev.get("pageId") !== lastEditedPageId) {
            const newParams = new URLSearchParams(prev);
            newParams.set("pageId", lastEditedPageId);
            return newParams;
          }
        } else if (
          !lastEditedPageId &&
          // NOTE (Bobby, 2025-08-18): Initial user messages sent through are "synthetic" messages and not actually from the db. This condition is to make sure
          // we don't clear the pageId from the initial sent messages.
          messages.some((m) => m.role === "assistant" || m.role === "tool") &&
          prev.has("pageId")
        ) {
          // Remove pageId if there's no lastEditedPageId
          const newParams = new URLSearchParams(prev);
          newParams.delete("pageId");
          return newParams;
        }
        return prev;
      });
    },
    [setSearchParams, messages],
  );

  /**
   * Handles the chat page update logic with deduplication
   */
  const handleChatPageUpdate = React.useCallback(
    (chatId: string, lastEditedPageId: string | null) => {
      const currentCombination = createChatPageCombination(
        chatId,
        lastEditedPageId,
      );

      // Only update if we haven't processed this combination before
      if (lastProcessedRef.current !== currentCombination) {
        lastProcessedRef.current = currentCombination;
        updatePageIdParams(lastEditedPageId);
      }
    },
    [updatePageIdParams],
  );

  React.useEffect(() => {
    if (chatId) {
      handleChatPageUpdate(chatId, lastEditedPageId);
    }
  }, [chatId, lastEditedPageId, handleChatPageUpdate]);

  return (
    <div className="space-y-0 flex flex-col h-full min-h-0 my-2 md:mb-5 gap-5 w-full max-w-[880px] mx-auto">
      {messages.length === 0 && !isChatLoading && !chatId && (
        <div className="flex flex-col h-full w-full min-h-0 items-center justify-center relative ">
          <IconWrapper size="lg" className="hidden md:flex">
            <Sparkle className="w-10 h-10" />
          </IconWrapper>
          <div className="header-h2 mt-2 md:mt-4">
            What can I help you with?
          </div>
          <div className="text-sm text-muted mb-7">
            Some quick actions for you to get started
          </div>
          <div className="grid grid-cols-2 gap-3 relative">
            {currentPlaceholderOptions.map((option, index) => (
              <PlaceholderOption
                key={index}
                {...option}
                editor={editor}
                onAction={() => {
                  logEvent("chat.selectRecommendationPrompt", {
                    origin: "chat",
                    prompt: option.prompt,
                  });
                  onQuickPrompt?.(option.prompt, option.action);
                }}
              />
            ))}
          </div>
        </div>
      )}
      {groupedMessages.map((group, index) => {
        const isLastAssistantGroup =
          group.role === "assistant" && index === groupedMessages.length - 1;

        return (
          <MessageGroup
            key={index}
            role={group.role}
            messages={group.messages}
            toolMessageId={group.toolMessageId}
            isChatLoading={isChatLoading}
            isLastAssistantGroup={isLastAssistantGroup}
          />
        );
      })}
      {isTyping && (
        <div className="flex flex-row items-center justify-start w-full">
          <img
            src="/images/chat/replo-chat.svg"
            alt="Replo Chat"
            width={24}
            height={24}
          />
          <TypingIndicator />
        </div>
      )}
    </div>
  );
}

const MessageGroup = ({
  role,
  messages,
  toolMessageId,
  isChatLoading,
  isLastAssistantGroup,
}: {
  role: "user" | "assistant";
  messages: ChatMessageType[];
  toolMessageId?: string;
  isChatLoading?: boolean;
  isLastAssistantGroup?: boolean;
}) => {
  return (
    <div className="flex flex-row items-start w-full">
      {role === "assistant" && (
        <img
          src="/images/chat/replo-chat.svg"
          className="mt-1.5"
          alt="Replo Chat"
          width={24}
          height={24}
        />
      )}
      <div
        className={twMerge(
          "flex flex-col w-full min-w-0",
          role === "user" && "items-end ml-8 gap-3",
        )}
      >
        {messages.map((message, index) => {
          return (
            <ChatMessage
              id={message.id}
              key={index}
              messageId={message.id}
              toolMessageId={toolMessageId}
              content={message.content}
              role={message.role}
            />
          );
        })}
        <ChatActions
          messages={messages}
          role={role}
          isChatLoading={isChatLoading}
          isLastAssistantGroup={isLastAssistantGroup}
        />
      </div>
    </div>
  );
};

const pagePlaceholderOptions = [
  {
    icon: <Message className="w-6 h-6" />,
    title: "Update Copy",
    subtitle: "Make text clearer, catchier, and match our branding.",
    prompt: "Clean up the copy on this page and have it match our brand",
  },
  {
    icon: <Paintbrush className="w-6 h-6" />,
    title: "Restyle",
    subtitle: "Change styling of page layout, fonts, and colors.",
    prompt: "Restyle the designs and formatting of this page",
  },
  {
    icon: <Image className="w-6 h-6" />,
    title: "Reference an Image",
    subtitle: "Match color, tone, or layout of an uploaded image.",
    prompt: "Reference this image to update the page",
    action: "upload",
  },
  {
    icon: <Shirt className="w-6 h-6" />,
    title: "Reference a Product",
    subtitle: "Add a product to make the content more relevant.",
    prompt: "Reference this product to update the page:",
    action: "product",
  },
];

const placeholderOptions = [
  {
    icon: <Page className="w-6 h-6" />,
    title: "Suggest Ideas to Build",
    subtitle:
      "We'll pull some templates to get you started — add a goal to get more tailored help.",
    prompt:
      "Suggest some templates we can use to build for different goals according to our brand",
  },
  {
    icon: <Image className="w-6 h-6" />,
    title: "Copy a page you like",
    subtitle:
      "Upload a screenshot of a page you love — we'll replicate it as an editable layout.",
    prompt:
      "Copy the layout and elements depicted in this attached screenshot as a full page",
    action: "upload",
  },
  {
    icon: <Megaphone className="w-6 h-6" />,
    title: "Build Landing Page from Ad",
    subtitle:
      "Drop in a screenshot — we'll generate a matching landing page that fits the asset.",
    prompt:
      "Build me a matching product landing page that fits well as the CTA to this ad screenshot",
    action: "upload",
  },
  {
    icon: <Shirt className="w-6 h-6" />,
    title: "Build a Product Page",
    subtitle:
      "Link a product you've set up — we'll design a product page with all the right context.",
    prompt:
      "Build me a suitable branded product page that helps me sell this product:",
    action: "product",
  },
];

/**
 * Groups consecutive chat messages by role for easier rendering.
 *
 * This function transforms a flat array of individual messages into grouped message objects.
 * User messages remain grouped as "user", while both "assistant" and "tool" messages are
 * grouped together under the "assistant" role since they represent the AI's response flow.
 *
 * @param messages - Array of individual chat messages from the database
 * @returns Array of message groups where consecutive messages of the same role type are combined
 *
 * @example
 * Input:
 * [
 *   { role: 'user', content: [] },
 *   { role: 'assistant', content: [] },
 *   { role: 'tool', content: [] },
 *   { role: 'assistant', content: [] }
 * ]
 *
 * Output:
 * [
 *   { role: "user", messages: [{ role: 'user', content: [] }] },
 *   {
 *     role: "assistant",
 *     messages: [
 *       { role: "assistant", content: [] },
 *       { role: "tool", content: [] },
 *       { role: "assistant", content: [] }
 *     ]
 *   }
 * ]
 */
const groupMessages = (messages: ChatMessageType[]): MessageGroup[] => {
  return messages.reduce<MessageGroup[]>((groups, message) => {
    // this makes everything not a user an assistant message (e.g. tool messages).
    const groupRole = message.role === "user" ? "user" : "assistant";
    const isToolMessage = message.role === "tool";
    const lastGroup = groups[groups.length - 1];

    // Check if any content item has isNewGroup flag
    const shouldStartNewGroup = message.content.some(
      (contentItem) =>
        contentItem.type === "text" && contentItem.isNewMessageGroup === true,
    );

    if (lastGroup?.role === groupRole && !shouldStartNewGroup) {
      lastGroup.messages.push(message);
      if (isToolMessage && !lastGroup.toolMessageId) {
        lastGroup.toolMessageId = message.id;
      }
    } else {
      groups.push({
        role: groupRole,
        messages: [message],
        ...(isToolMessage && { toolMessageId: message.id }),
      });
    }

    return groups;
  }, []);
};

// This function is used to look through chat messages and find which page to show in the editor (if any). We take the first of the following:
//  - Last edited page
//  - Last created page from a selected template
const useFindLastEditedPageIdFromMessages = (
  messages: ChatMessageType[],
): string | null => {
  const projectId = useCurrentProjectId();

  // Find the most recent message with either fileVersionId or selectedTemplate fileId
  const result = React.useMemo(() => {
    // Note (Noah, 2025-08-19): Sort messages so that the most recent message is first, since
    // we want to make sure that the page the user has most recently edited using the chat is
    // the one that's returned from this function
    const sortedMessages = [...messages].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    );
    for (const message of sortedMessages) {
      if (message.content.some((content) => content.fileId)) {
        for (const content of message.content) {
          if (content.fileId) {
            return { type: "fileId", id: content.fileId };
          }
        }
      }

      // TODO (Bobby, 2025-08-19): All these cases below aren't actually needed for any new chats now that the above check is in. Keeping them here for now just so
      // it doesn't cause any weird behavior for old chats. Can remove this after a bit so old chats are kind of pushed out. Have ticket to remove this next week.

      // Check for revert message with fileVersionId
      if (message.role === "assistant") {
        for (const content of message.content) {
          if (content.type === "text" && content.contentType === "revert") {
            const parsed = parseJsonOrNull(content.text);
            if (
              parsed &&
              typeof parsed === "object" &&
              "fileVersionId" in parsed &&
              parsed.fileVersionId &&
              typeof parsed.fileVersionId === "string"
            ) {
              return { type: "fileVersion", id: parsed.fileVersionId };
            }
          }
        }
      }

      // Check for tool message with fileVersionId
      if (message.role === "tool") {
        for (const content of message.content) {
          if (
            content.type === "tool-result" &&
            "fileVersionId" in content.output.value &&
            content.output.value.fileVersionId
          ) {
            return {
              type: "fileVersion",
              id: content.output.value.fileVersionId,
            };
          }

          if (
            content.type === "tool-result" &&
            "fileId" in content.output.value &&
            content.output.value.fileId
          ) {
            return { type: "fileId", id: content.output.value.fileId };
          }
        }
      }

      // Check for user message with selectedTemplate
      if (message.role === "user") {
        for (const content of message.content) {
          if (
            content.type === "text" &&
            content.contentType === "selectedTemplate"
          ) {
            const parsed = parseJsonOrNull(content.text);
            if (
              parsed &&
              typeof parsed === "object" &&
              "fileId" in parsed &&
              parsed.fileId &&
              typeof parsed.fileId === "string"
            ) {
              return { type: "fileId", id: parsed.fileId };
            }
          }
        }
      }
    }
    return null;
  }, [messages]);

  // Fetch the file version using trpc if we found a fileVersionId
  const { data: fileVersion } = fileVersionRouter.get.useQuery(
    result?.type === "fileVersion" && projectId
      ? {
          id: result.id,
          projectId,
          shouldIncludeDeleted: true,
        }
      : skipToken,
  );

  // Return fileId directly if selectedTemplate, or from fileVersion if tool message
  if (result?.type === "fileId") {
    return result.id;
  }
  return fileVersion?.fileId ?? null;
};

const createChatPageCombination = (chatId: string, pageId: string | null) => {
  return `${chatId}:${pageId ?? "none"}`;
};
